import { Card } from '../design-system/Card';
import { Text } from '../design-system/Text';
import { Button } from '../design-system/Button';
import { useNudgeImpression } from '../../hooks/useNudgeImpression';
import { analytics } from '../../lib/analytics';
import { Zap, TrendingDown, X } from 'lucide-react';
import { formatCurrency } from '../../utils/format';
import type { FastagProfile } from '../../types';

interface NudgeInlineCardProps {
  profile: FastagProfile;
  visible: boolean;
  onRecharge: () => void;
  onDismiss: () => void;
  dismissed: boolean;
}

export function NudgeInlineCard({
  profile,
  visible,
  onRecharge,
  onDismiss,
  dismissed,
}: NudgeInlineCardProps) {
  useNudgeImpression('nudge_inline_card', visible && !dismissed, { balance: profile.balance });

  if (!visible || dismissed) return null;

  const isPredicted = profile.predictedLow && profile.balance >= 0;
  const title = isPredicted ? 'FASTag balance may not last' : 'FASTag balance is low';
  const body = isPredicted
    ? `Based on your recent toll usage, your ₹${profile.balance} balance may run out soon. Top up to avoid delays.`
    : `Your FASTag balance is ${formatCurrency(profile.balance)}. Add ${formatCurrency(profile.recommendedAmount)} to keep tolls running smoothly.`;

  const handleRecharge = () => {
    analytics.emit('recharge_cta_click', { nudge_id: 'nudge_inline_card', balance: profile.balance });
    onRecharge();
  };

  const handleDismiss = () => {
    analytics.emit('nudge_dismiss', { nudge_id: 'nudge_inline_card' });
    onDismiss();
  };

  return (
    <Card
      padding="md"
      elevation={2}
      className="mx-4 border-l-4 border-l-park-500 bg-gradient-to-r from-park-50 to-white dark:from-park-900/20 dark:to-slate-800"
      data-testid="nudge-inline-card"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-park-100 text-park-600 dark:bg-park-900/40 dark:text-park-400">
          {isPredicted ? <TrendingDown className="h-5 w-5" /> : <Zap className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <Text variant="body-medium">{title}</Text>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss FASTag recharge nudge"
              className="shrink-0 rounded-md p-1 text-slate-400 hover:text-slate-600 focus-visible:ring-2 touch-target"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <Text variant="caption" color="secondary" className="mt-1">{body}</Text>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" onClick={handleRecharge} aria-label="Recharge FASTag">
              Recharge {formatCurrency(profile.recommendedAmount)}
            </Button>
            <Button size="sm" variant="ghost" onClick={handleDismiss}>
              Dismiss
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
